// BestWorstSalesReport.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
// --- HELPERS -------------------------------
const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10); // yyyy-mm-dd

const COLUMNS = [
  { key: 'Category', label: 'Category' },
  { key: 'ItemName', label: 'Item Name' },
  { key: 'QtySold', label: 'Qty Sold', align: 'right' },
  { key: 'TotalSales', label: 'Total Sales', align: 'right' },
];

// Custom order for Category column
const categoryOrder = (cat) => {
  switch (cat) {
    case 'Best Sale Item':
      return 1;
    case 'Average Sale Items':
      return 2;
    case 'Worst Sale Items':
      return 3;
    default:
      return 99;
  }
};

// --- COMPONENT -------------------------------
const BestWorstSalesReport = ({ Currency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
  });

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  // ------------------------------------------------------------
  // Fetch Branches
  // ------------------------------------------------------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];

      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ------------------------------------------------------------
  // Fetch Best & Worst Sales Data
  // ------------------------------------------------------------
  const fetchData = async (page = pagination.currentPage, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // params.append('PageNo', page);
      // params.append('PageSize', size);
      params.append('ReportType', 'Best and Least Sale'); // ⭐ important
      params.append('Measure', 'Revenue');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');
      params.append('BranchId', selectedBranch?.value || 0); // 0 = all branches

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      const normalized = result.map((r) => ({
        Category: r['Category'] || '—',
        ItemName: r['Item Name'] || '—',
        QtySold: +r['Qty Sold'] || 0,
        TotalSales: +r['Total Sales'] || 0,
      }));

      setRows(normalized);

      setPagination((p) => ({
        ...p,
        ...(res?.data?.paginationData || {}),
        totalRecords: normalized.length,
        currentPage: page,
        pageSize: size,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // ------------------------------------------------------------
  // Sorting Logic
  // ------------------------------------------------------------
  const [sortState, setSortState] = useState({ key: 'Category', dir: 'asc' });

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...rows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      // special custom sort for Category
      if (key === 'Category') {
        const ac = categoryOrder(av);
        const bc = categoryOrder(bv);
        return dir === 'asc' ? ac - bc : bc - ac;
      }

      // numeric sort if both numeric
      if (Number.isFinite(+av) && Number.isFinite(+bv)) {
        return dir === 'asc' ? av - bv : bv - av;
      }

      // fallback string sort
      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [rows, sortState]);

  // Totals row
  const totals = useMemo(
    () => ({
      qty: sortedRows.reduce((s, r) => s + r.QtySold, 0),
      sales: sortedRows.reduce((s, r) => s + r.TotalSales, 0),
    }),
    [sortedRows],
  );

  // Handlers
  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  // CSV Export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows
      .map((r) =>
        COLUMNS.map((c) => {
          const val = r[c.key];
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(','),
      )
      .join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `best-worst-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const exportExcel = () => {
    if (!sortedRows.length) return;

    // Header
    const header = COLUMNS.map((c) => c.label);

    // Body (numbers as numbers for Excel)
    const body = sortedRows.map((r) =>
      COLUMNS.map((c) => {
        const v = r?.[c.key];

        if (c.key === 'QtySold') return Number(v) || 0;
        if (c.key === 'TotalSales') return Number(v) || 0;

        return String(v ?? '').trim(); // Category, ItemName
      }),
    );

    // Totals row (matches your footer)
    const totalsRow = ['Total', '—', Number(totals.qty) || 0, Number(totals.sales) || 0];

    const aoa = [header, ...body, totalsRow];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Optional widths
    ws['!cols'] = [
      { wch: 20 }, // Category
      { wch: 36 }, // Item Name
      { wch: 12 }, // Qty Sold
      { wch: 18 }, // Total Sales
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Best Worst Sales');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `best-worst-sales_${branchName}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };
  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* <h4 className="mb-3">Best &amp; Lea Sales</h4> */}

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="Select Branch"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-4">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={index}>
                    <td>{r.Category}</td>
                    <td>{r.ItemName}</td>
                    <td className="text-end">{r.QtySold}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.TotalSales)}
                    </td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td>Total</td>
                  <td>—</td>
                  <td className="text-end">{totals.qty}</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.sales)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(BestWorstSalesReport);
